/**
 * Calendar Store
 *
 * Zustand (vanilla) store backing the read-only Calendar page. Calendar data is
 * fetched and cached one month at a time (keyed `YYYY-MM`) so navigating
 * between weeks never refetches — only crossing into an un-loaded month does. A
 * week that straddles a month boundary loads both months (see
 * `ensureRangeLoaded`). Cached months stay fresh for `CACHE_TTL_MS`.
 *
 * The store owns fetching + caching only. Deriving the events for a given week
 * or month (slicing, grouping) is done by the pure helpers in
 * `utils/calendar.ts`, so views compute reactively from `monthsByKey`.
 */

import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { registerSessionScoped } from './session-scope';
import { calendarService } from '../services/calendar.service';
import { getMonthKeysInRange } from '../utils/calendar';
import { dayjs } from '../utils/date';

import type { CalendarEvent } from '../types/domain/calendar.types';
import type { DateInput } from '../utils/date';

// =============================================================================
// CONSTANTS
// =============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000;

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface CalendarMonthCacheEntry {
  events: CalendarEvent[];
  /** Epoch ms when this month was fetched, for TTL freshness checks. */
  fetchedAt: number;
}

export interface CalendarStoreState {
  /** Cached events keyed by month (`YYYY-MM`). */
  monthsByKey: Record<string, CalendarMonthCacheEntry>;
  /** Which month keys currently have an in-flight request. */
  loadingMonths: Record<string, boolean>;
  /** Last fetch error per month key (`null` when the last fetch succeeded). */
  errorsByMonth: Record<string, string | null>;
}

export interface CalendarStoreActions {
  /** Fetch and cache a single month. No-ops if the month is already fresh. */
  fetchMonth: (
    username: string,
    monthKey: string,
    options?: { force?: boolean }
  ) => Promise<void>;
  /**
   * Ensure every month touched by the inclusive `[from, to]` range is loaded.
   */
  ensureRangeLoaded: (
    username: string,
    from: DateInput,
    to: DateInput,
    options?: { force?: boolean }
  ) => Promise<void>;
  /** Reset the store to its initial empty state. */
  reset: () => void;
}

export type CalendarStore = CalendarStoreState & CalendarStoreActions;

// =============================================================================
// HELPERS
// =============================================================================

const createInitialState = (): CalendarStoreState => ({
  monthsByKey: {},
  loadingMonths: {},
  errorsByMonth: {},
});

const isFresh = (
  entry: CalendarMonthCacheEntry | undefined,
  now: number
): boolean => entry !== undefined && now - entry.fetchedAt < CACHE_TTL_MS;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const calendarStore = createStore<CalendarStore>((set, get) => ({
  ...createInitialState(),

  fetchMonth: async (username, monthKey, options): Promise<void> => {
    const force = options?.force ?? false;
    const state = get();

    // Skip when the month is already cached-fresh or a request is in flight.
    if (!force && isFresh(state.monthsByKey[monthKey], dayjs().valueOf()))
      return;
    if (!force && state.loadingMonths[monthKey]) return;

    set((prev) => ({
      loadingMonths: { ...prev.loadingMonths, [monthKey]: true },
      errorsByMonth: { ...prev.errorsByMonth, [monthKey]: null },
    }));

    try {
      const events = await calendarService.getCalendarEvents(
        username,
        monthKey
      );
      set((prev) => ({
        monthsByKey: {
          ...prev.monthsByKey,
          [monthKey]: { events, fetchedAt: dayjs().valueOf() },
        },
      }));
    } catch (error) {
      const message = failureMessage(error, 'Failed to load calendar events');
      set((prev) => ({
        errorsByMonth: { ...prev.errorsByMonth, [monthKey]: message },
      }));
    } finally {
      set((prev) => ({
        loadingMonths: { ...prev.loadingMonths, [monthKey]: false },
      }));
    }
  },

  ensureRangeLoaded: async (username, from, to, options): Promise<void> => {
    const keys = getMonthKeysInRange(from, to);
    await Promise.all(
      keys.map((key) => get().fetchMonth(username, key, options))
    );
  },

  reset: (): void => set(createInitialState()),
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectCalendarMonths = (
  state: CalendarStore
): Record<string, CalendarMonthCacheEntry> => state.monthsByKey;

export const selectCalendarLoadingMonths = (
  state: CalendarStore
): Record<string, boolean> => state.loadingMonths;

export const selectCalendarErrorsByMonth = (
  state: CalendarStore
): Record<string, string | null> => state.errorsByMonth;

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('calendarStore', () => {
  calendarStore.getState().reset();
});
